import { FC, useState, type MouseEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Snackbar, Typography } from '@mui/material';

import styles from './IdeaCard.module.scss';
import { DateIcon, DeleteIcon, EditIcon, MarkerPinIcon, MergeIcon, RecentlyEditedIcon, TripIcon } from '../../../../../components/Icons';
import ideaCoverFallback from '../../../../../assets/images/TripMockDetails.jpg';
import { Idea } from '../../../../../types/idea.types';
import { Trip } from '../../../../../types/trip.types';
import ThreeDotsMenu, { MenuItemConfig } from '../../../../../components/ThreeDotsMenu/ThreeDotsMenu';
import CoverUploadModal from '../../../../../common/CoverUploadModal/CoverUploadModal';
import ConfirmActionModal from '../../../../../common/ConfirmActionModal/ConfirmActionModal';
import CreateTripModal from '../../../MyTrips/components/CreateTripModal';
import {
  useDeleteIdeaCustomCoverMutation,
  useUploadIdeaCustomCoverMutation,
} from '../../../../../store/services/ideasApi';

type PendingConfirm = null | 'cover';

interface IdeaCardProps {
  idea: Idea;
  onDeleteRequest?: (idea: Idea) => void;
  onEdit?: (idea: Idea) => void;
  onRefetchIdeas?: () => void;
}

const IdeaCard: FC<IdeaCardProps> = ({ idea, onDeleteRequest, onEdit, onRefetchIdeas }) => {
  const navigate = useNavigate();
  const [showCoverModal, setShowCoverModal] = useState(false);
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm>(null);
  const [createTripModalOpen, setCreateTripModalOpen] = useState(false);
  const [createTripToastOpen, setCreateTripToastOpen] = useState(false);
  const [createTripToastMessage, setCreateTripToastMessage] = useState('');
  const [uploadIdeaCover] = useUploadIdeaCustomCoverMutation();
  const [deleteIdeaCustomCover, { isLoading: isDeletingCover }] = useDeleteIdeaCustomCoverMutation();
  const coverUrl = idea.custom_cover;

  const durationDays = idea.duration_in_days && idea.duration_in_days > 0 ? idea.duration_in_days : 0;

  const handleCardClick = () => {
    navigate(`/ideas/${idea.id}`);
  };

  const handleEditClick = (event: MouseEvent) => {
    event.stopPropagation();
    onEdit?.(idea);
  };

  const handleDeleteClick = (event: MouseEvent) => {
    event.stopPropagation();
    onDeleteRequest?.(idea);
  };

  const performCoverDelete = async () => {
    try {
      await deleteIdeaCustomCover(idea.id).unwrap();
      setPendingConfirm(null);
      onRefetchIdeas?.();
    } catch (error) {
      console.error('Failed to delete idea cover:', error);
    }
  };

  const menuItems: MenuItemConfig[] = [
    {
      id: 'idea-cover-upload',
      label: coverUrl ? 'Change cover' : 'Upload cover',
      icon: <EditIcon />,
      disabled: isDeletingCover,
      onClick: event => {
        event.stopPropagation();
        setShowCoverModal(true);
      },
    },
    ...(coverUrl
      ? [
          {
            id: 'idea-cover-delete',
            label: 'Delete cover',
            icon: <DeleteIcon />,
            variant: 'danger' as const,
            disabled: isDeletingCover,
            onClick: (event: MouseEvent) => {
              event.stopPropagation();
              setPendingConfirm('cover');
            },
          },
        ]
      : []),
    {
      id: 'create-trip-from-idea',
      label: 'Create Trip',
      icon: <TripIcon size={24} />,
      onClick: (event: MouseEvent) => {
        event.stopPropagation();
        setCreateTripModalOpen(true);
      },
    },
    {
      id: 'edit',
      label: 'Edit Idea',
      icon: <RecentlyEditedIcon />,
      onClick: handleEditClick,
    },
    {
      id: 'delete',
      label: 'Delete Idea',
      icon: <DeleteIcon />,
      variant: 'danger',
      onClick: handleDeleteClick,
    },
  ];

  return (
    <>
      <Box
        className={styles.ideaCard}
        onClick={handleCardClick}
      >
        <Box className={styles.ideaImageContainer}>
          <Box
            className={styles.ideaImage}
            sx={{
              backgroundImage: `url(${coverUrl || ideaCoverFallback})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
            }}
          >
            <Box
              className={styles.menuWrapper}
              onClick={event => event.stopPropagation()}
              onMouseDown={event => event.stopPropagation()}
            >
              <ThreeDotsMenu
                menuItems={menuItems}
                iconSize={28}
                iconClassName={styles.menuIconOnImage}
              />
            </Box>
          </Box>
        </Box>

        <Box className={styles.ideaContent}>
          <Box className={styles.ideaHeader}>
            <Typography
              variant="h9Semibold"
              className={styles.ideaTitle}
            >
              {idea.name}
            </Typography>
          </Box>

          <Box className={styles.ideaDetails}>
            <Box className={styles.ideaStops}>
              <MarkerPinIcon size={24} />
              <Typography variant="h11">
                {idea.stops_count} {idea.stops_count === 1 ? 'stop' : 'stops'}
              </Typography>
            </Box>

            <Box className={styles.ideaDuration}>
              <DateIcon size={24} />
              <Typography variant="h11">
                {durationDays} {durationDays === 1 ? 'day' : 'days'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <CoverUploadModal
        open={showCoverModal}
        onClose={() => setShowCoverModal(false)}
        title="Update Idea Cover"
        description="Choose a new image for your idea cover."
        placeholder={{
          title: 'Select your idea cover',
          subtitle: '(e.g., PNG, JPG, WEBP up to 5MB)',
        }}
        uploadFile={file =>
          uploadIdeaCover({ ideaId: idea.id, file })
            .unwrap()
            .then(() => {
              onRefetchIdeas?.();
            })
        }
      />

      <ConfirmActionModal
        open={pendingConfirm !== null}
        onClose={() => setPendingConfirm(null)}
        onConfirm={performCoverDelete}
        description="Do you really want to delete the cover image?"
        confirmLabel="Delete"
        isConfirming={isDeletingCover}
      />

      <CreateTripModal
        open={createTripModalOpen}
        onClose={() => setCreateTripModalOpen(false)}
        onSuccess={(trip?: Trip) => {
          if (trip?.id == null) return;
          const tripName = trip.name?.trim() || 'Your trip';
          setCreateTripToastMessage(`Trip '${tripName}' was successfully created from your idea.`);
          setCreateTripToastOpen(true);
          window.setTimeout(() => {
            setCreateTripToastOpen(false);
            navigate(`/trips/${trip.id}`, {
              state: {
                createdFromIdea: true,
                ideaToTripPreToast: true,
                createdTripName: trip.name,
                sourceIdeaName: idea.name,
              },
            });
          }, 1400);
        }}
        sourceIdeaId={idea.id}
        initialTripName={idea.name}
      />

      <Snackbar
        open={createTripToastOpen}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        sx={{ zIndex: theme => theme.zIndex.modal + 2 }}
      >
        <Alert
          severity="success"
          variant="filled"
          sx={{ width: '100%', maxWidth: 480 }}
        >
          {createTripToastMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default IdeaCard;
